import type { BillingPeriod, PricingCatalog } from '@horevia/contracts';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Container,
  ErrorState,
  Input,
  NoPermissionState,
  PageHeader,
  Skeleton,
  Table,
  TableWrap,
  Td,
  Th,
  Tr,
  toast,
} from '@horevia/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { Save } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSession } from '@/features/auth/session';
import {
  billingKeys,
  pricingQueryOptions,
  updatePricing,
} from '@/features/subscription-billing/queries';
import { errorMessage, errorTraceId } from '@/lib/errors';

export const Route = createFileRoute('/_app/platform/pricing')({ component: PricingPage });

type PriceDraft = Record<string, { MONTHLY: string; YEARLY: string }>;
type LimitDraft = Record<string, { properties: string; resources: string; users: string }>;

const PERIODS: BillingPeriod[] = ['MONTHLY', 'YEARLY'];

/** Tarifs et limites du catalogue (ADR-023, R-PAY-07), par devise ; aucun prix dans le code. */
function PricingPage() {
  const { t } = useTranslation(['platform', 'common', 'errors', 'settings']);
  const session = useSession();
  const queryClient = useQueryClient();
  const canRead = session.can('platform.read_health');
  const canManage = session.can('platform.manage_billing');
  const query = useQuery(pricingQueryOptions(canRead));
  const [currency, setCurrency] = useState(session.user.tenant.currencyCode);
  const [plans, setPlans] = useState<PriceDraft>({});
  const [modules, setModules] = useState<PriceDraft>({});
  const [limits, setLimits] = useState<LimitDraft>({});

  useEffect(() => {
    if (!query.data) return;
    const pick = (prices: { currencyCode: string; billingPeriod: string; amount: number }[]) => ({
      MONTHLY: String(
        prices.find((p) => p.currencyCode === currency && p.billingPeriod === 'MONTHLY')?.amount ??
          '',
      ),
      YEARLY: String(
        prices.find((p) => p.currencyCode === currency && p.billingPeriod === 'YEARLY')?.amount ??
          '',
      ),
    });
    setPlans(
      Object.fromEntries(query.data.plans.map((plan) => [plan.planCode, pick(plan.prices)])),
    );
    setModules(
      Object.fromEntries(
        query.data.modules.map((module) => [module.moduleCode, pick(module.prices)]),
      ),
    );
    setLimits(
      Object.fromEntries(
        query.data.plans.map((plan) => [
          plan.planCode,
          {
            properties: plan.limits.properties === null ? '' : String(plan.limits.properties),
            resources: plan.limits.resources === null ? '' : String(plan.limits.resources),
            users: plan.limits.users === null ? '' : String(plan.limits.users),
          },
        ]),
      ),
    );
  }, [query.data, currency]);

  const save = useMutation({
    mutationFn: () => {
      const catalog = query.data as PricingCatalog;
      const merged = (
        prices: { currencyCode: string; billingPeriod: BillingPeriod; amount: number }[],
        draft: { MONTHLY: string; YEARLY: string },
      ) => [
        ...prices.filter((price) => price.currencyCode !== currency),
        ...PERIODS.flatMap((period) =>
          draft[period].trim() === '' || Number.isNaN(Number(draft[period]))
            ? []
            : [{ currencyCode: currency, billingPeriod: period, amount: Number(draft[period]) }],
        ),
      ];
      const limitOf = (value: string) => (value.trim() === '' ? null : Math.max(1, Number(value)));
      return updatePricing({
        plans: catalog.plans.map((plan) => ({
          planCode: plan.planCode,
          limits: {
            properties: limitOf(limits[plan.planCode]?.properties ?? ''),
            resources: limitOf(limits[plan.planCode]?.resources ?? ''),
            users: limitOf(limits[plan.planCode]?.users ?? ''),
          },
          prices: merged(plan.prices, plans[plan.planCode] ?? { MONTHLY: '', YEARLY: '' }),
        })),
        modules: catalog.modules.map((module) => ({
          moduleCode: module.moduleCode,
          prices: merged(module.prices, modules[module.moduleCode] ?? { MONTHLY: '', YEARLY: '' }),
        })),
      });
    },
    onSuccess: async () => {
      toast.success(t('platform:pricing.toasts.saved'));
      await queryClient.invalidateQueries({ queryKey: billingKeys.pricing });
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });

  if (!canRead) {
    return (
      <Container>
        <NoPermissionState
          title={t('common:states.noPermissionTitle')}
          description={t('common:states.noPermissionDescription')}
        />
      </Container>
    );
  }

  const priceInput = (
    draft: PriceDraft,
    setDraft: (next: PriceDraft) => void,
    code: string,
    period: BillingPeriod,
    label: string,
  ) => (
    <Input
      aria-label={label}
      type="number"
      min={0}
      step="any"
      disabled={!canManage}
      value={draft[code]?.[period] ?? ''}
      onChange={(event) =>
        setDraft({
          ...draft,
          [code]: { ...(draft[code] ?? { MONTHLY: '', YEARLY: '' }), [period]: event.target.value },
        })
      }
      style={{ maxWidth: 140 }}
    />
  );

  return (
    <Container>
      <PageHeader
        title={t('platform:pricing.title')}
        subtitle={t('platform:pricing.subtitle')}
        breadcrumb={[
          { label: t('platform:title'), link: <Link to="/platform">{t('platform:title')}</Link> },
          { label: t('platform:pricing.title') },
        ]}
        actions={
          canManage ? (
            <Button
              variant="primary"
              disabled={save.isPending || !query.data}
              onClick={() => save.mutate()}
            >
              <Save className="ico" aria-hidden="true" />
              {t('platform:pricing.save')}
            </Button>
          ) : null
        }
      />
      {query.isPending ? (
        <Skeleton height={240} />
      ) : query.isError || !query.data ? (
        <ErrorState
          title={t('common:states.errorTitle')}
          description={errorMessage(query.error, t)}
          traceId={errorTraceId(query.error)}
          retryLabel={t('common:actions.retry')}
          onRetry={() => void query.refetch()}
          traceCopyLabel={t('common:actions.copy')}
          traceCopiedLabel={t('common:actions.copied')}
        />
      ) : (
        <div className="stack">
          <div className="row" style={{ gap: 8, alignItems: 'center' }}>
            <label className="t-12 muted" htmlFor="pricing-currency">
              {t('platform:pricing.currency')}
            </label>
            <Input
              id="pricing-currency"
              value={currency}
              maxLength={3}
              onChange={(event) => setCurrency(event.target.value.toUpperCase())}
              style={{ maxWidth: 100, textTransform: 'uppercase' }}
            />
          </div>
          <Card>
            <CardHeader title={t('platform:pricing.plans')} />
            <CardBody density="flush">
              <TableWrap>
                <Table>
                  <thead>
                    <tr>
                      <Th>{t('platform:pricing.columns.plan')}</Th>
                      <Th>{t('platform:pricing.columns.monthly')}</Th>
                      <Th>{t('platform:pricing.columns.yearly')}</Th>
                      <Th>{t('platform:pricing.columns.properties')}</Th>
                      <Th>{t('platform:pricing.columns.resources')}</Th>
                      <Th>{t('platform:pricing.columns.users')}</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {query.data.plans.map((plan) => (
                      <Tr key={plan.planCode} data-testid={`pricing-plan-${plan.planCode}`}>
                        <Td>
                          <strong>{plan.planName}</strong>
                        </Td>
                        <Td>
                          {priceInput(
                            plans,
                            setPlans,
                            plan.planCode,
                            'MONTHLY',
                            `${plan.planName} ${t('platform:pricing.columns.monthly')}`,
                          )}
                        </Td>
                        <Td>
                          {priceInput(
                            plans,
                            setPlans,
                            plan.planCode,
                            'YEARLY',
                            `${plan.planName} ${t('platform:pricing.columns.yearly')}`,
                          )}
                        </Td>
                        {(['properties', 'resources', 'users'] as const).map((key) => (
                          <Td key={key}>
                            <Input
                              aria-label={`${plan.planName} ${t(`platform:pricing.columns.${key}`)}`}
                              type="number"
                              min={1}
                              disabled={!canManage}
                              value={limits[plan.planCode]?.[key] ?? ''}
                              onChange={(event) =>
                                setLimits({
                                  ...limits,
                                  [plan.planCode]: {
                                    ...(limits[plan.planCode] ?? {
                                      properties: '',
                                      resources: '',
                                      users: '',
                                    }),
                                    [key]: event.target.value,
                                  },
                                })
                              }
                              style={{ maxWidth: 100 }}
                            />
                          </Td>
                        ))}
                      </Tr>
                    ))}
                  </tbody>
                </Table>
              </TableWrap>
              <p className="t-12 muted" style={{ padding: '8px 16px' }}>
                {t('platform:pricing.priceHint')} {t('platform:pricing.limitHint')}
              </p>
            </CardBody>
          </Card>
          <Card>
            <CardHeader title={t('platform:pricing.modules')} />
            <CardBody density="flush">
              <TableWrap>
                <Table>
                  <thead>
                    <tr>
                      <Th>{t('platform:pricing.columns.module')}</Th>
                      <Th>{t('platform:pricing.columns.monthly')}</Th>
                      <Th>{t('platform:pricing.columns.yearly')}</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {query.data.modules.map((module) => (
                      <Tr key={module.moduleCode}>
                        <Td>{module.moduleName}</Td>
                        <Td>
                          {priceInput(
                            modules,
                            setModules,
                            module.moduleCode,
                            'MONTHLY',
                            `${module.moduleName} ${t('platform:pricing.columns.monthly')}`,
                          )}
                        </Td>
                        <Td>
                          {priceInput(
                            modules,
                            setModules,
                            module.moduleCode,
                            'YEARLY',
                            `${module.moduleName} ${t('platform:pricing.columns.yearly')}`,
                          )}
                        </Td>
                      </Tr>
                    ))}
                  </tbody>
                </Table>
              </TableWrap>
            </CardBody>
          </Card>
        </div>
      )}
    </Container>
  );
}
